<script setup lang="ts">
import {ChipVariation, JobType} from "~/types/constants";
import {EVENT_TRACKING} from "~/constants/wadConstants";
import {useWadObserver} from "~/composables/useWadObserver";

const {
  job,
  linkTarget = '_blank',
  clickable = true,
  small = false,
} = defineProps<{
  job: JobLight,
  linkTarget?: string
  clickable?: boolean
  small?: boolean
}>();

const { fixedWidthImageParams } = useImageCdn();
const { releasedAgo } = useConverters();
const { trackEvent } = useTracking();
const config = useRuntimeConfig()

const maxSkillsToShow = computed(() => {
  return 3 + (job.remote ? 0 : 1) + (job.salary ? 0 : 1);
})
const limitedSkills = computed(() => {
  return job.skills.slice().sort((a, b) => a.length - b.length).slice(0, Math.min(maxSkillsToShow.value, job.skills.length));
});
const otherSkillsCount = computed(() => {
  return job.skills.length > maxSkillsToShow.value ? job.skills.length - maxSkillsToShow.value : 0;
})
const jobUrl = computed(() => {
  switch (job.job_type) {
    case JobType.Job:
      return `${config.public.wadPlatformBaseUrl}/en/companies/${job.company_id}/${job.company_slug}/${job.id}/${job.slug}`;
    case JobType.JobListing:
      return job.job_url;
    default:
      return null;
  }
});
const hasLink = computed(() => {
  return clickable && jobUrl.value;
});
const trackingData = computed(() => {
  switch (job.job_type) {
    case JobType.Job:
      return {job_id: job.id};
    case JobType.JobListing:
      return  {job_listing_id: job.id};
    default:
      return null;
  }
})

const onCardClick = (e) => {
  e.preventDefault();
  if(hasLink) {
    switch (job.job_type) {
      case JobType.Job:
        trackEvent(EVENT_TRACKING.JOB_CARD_DETAILS_CLICK, trackingData.value);
        break;
      case JobType.JobListing:
        trackEvent(EVENT_TRACKING.JOB_LISTING_CARD_APPLY_CLICK, trackingData.value);
        trackEvent(EVENT_TRACKING.JOB_LISTING_APPLY_EXTERNAL, trackingData.value);
        break;
    }
    window.open(jobUrl.value, linkTarget);
  }
}

const cardRef = useTemplateRef('jobCardRef')
useWadObserver(cardRef, () => {
  switch (job.job_type) {
    case JobType.Job:
      trackEvent(EVENT_TRACKING.JOB_CARD_VIEW, trackingData.value);
      break;
    case JobType.JobListing:
      trackEvent(EVENT_TRACKING.JOB_LISTING_VIEW, trackingData.value);
      break;
  }
});

const rc = useRuntimeConfig();

const coverImageUrl = computed(() => {
  const cdn = rc.public.wadCdnRoot || '';
  return job.cover_url || `${cdn}/public/default-job-listing-cover.png`;

});

const logoImageUrl = computed(() => {
  const cdn = rc.public.wadCdnRoot || '';
  return job.logo_url || `${cdn}/public/default-job-listing-logo.png`;
});

</script>

<template>
<article ref="jobCardRef" class="wad4-job-card" :class="{'wad4-job-card--clickable': hasLink}" @click="onCardClick">
  <div v-if="!small" class="wad4-job-card__graphic">
    <img class="wad4-job-card__graphic-cover" v-bind="fixedWidthImageParams(coverImageUrl, 400, 3.55)" loading="lazy" :alt="job.title"/>
    <div class="wad4-job-card__logo wad4-job-card__logo--on-graphic">
      <img class="wad4-job-card__logo-img" v-bind="fixedWidthImageParams(logoImageUrl, 44, 1)" loading="lazy" :alt="job.company_name" />
    </div>
  </div>
  <div class="wad4-job-card__body" :class="{'wad4-job-card__body--small': small}">
    <time class="wad4-job-card__time" :datetime="job.last_published_at">{{releasedAgo(job.last_published_at)}}</time>
    <a v-if="hasLink" class="wad4-job-card__link" :href="jobUrl" :target="linkTarget">
      <h3 class="wad4-job-card__title" :class="{'wad4-job-card__title--small': small}">{{job.title}}</h3>
    </a>
    <h3 v-else class="wad4-job-card__title" :class="{'wad4-job-card__title--small': small}">{{job.title}}</h3>
    <div class="wad4-job-card__data">
      <div class="wad4-job-card__logo wad4-job-card__logo--small" v-if="small && job.logo_url">
        <img class="wad4-job-card__logo-img wad4-job-card__logo-img--small" v-bind="fixedWidthImageParams(job.logo_url, 44, 1)" loading="lazy" :alt="job.company_name" />
      </div>
      <p class="wad4-job-card__info">{{job.company_name}}<br v-if="job.location"><span v-if="job.location" class="wad4-job-card__info--light">{{job.location}}</span></p>
    </div>
    <div class="wad4-job-card__chips">
      <CommonChip v-if="job.remote" :variation="ChipVariation.Remote">Remote</CommonChip>
      <CommonChip v-if="job.salary && !small" :variation="ChipVariation.Salary" :currency="job.currency">{{job.salary}}</CommonChip>
      <CommonChip v-for="seniority in job.seniorities" :key="seniority" :variation="ChipVariation.Seniority" :seniority="seniority.toLowerCase()">
        {{seniority}}
      </CommonChip>
      <CommonChip v-if="!small" v-for="skill in limitedSkills" :variation="ChipVariation.Skill" :key="skill">
        {{skill}}
      </CommonChip>
      <CommonChip v-if="!small && (otherSkillsCount > 0)" :variation="ChipVariation.Skill">+{{otherSkillsCount}}</CommonChip>
    </div>
  </div>
</article>
</template>

<style lang="scss">

  .wad4-job-card {
    display: flex;
    flex-direction: column;
    border-radius: $wad4-border-radius-xl;
    border: 1px solid $wad4-c-border-border-secondary;
    background: $wad4-c-background-bg-primary;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.10), 0 1px 2px 0 rgba(0, 0, 0, 0.06);


    &--clickable:hover {
      cursor: pointer;
      background: $wad4-c-background-bg-primary-hover;
    }

    &__graphic {
      width: 100%;
      aspect-ratio: 3.55;
      position: relative;
      border-top-left-radius:  $wad4-border-radius-xl;
      border-top-right-radius:  $wad4-border-radius-xl;

      &-cover {
        width: 100%;
        border-top-left-radius:  $wad4-border-radius-xl;
        border-top-right-radius:  $wad4-border-radius-xl;
        display: block;
      }
    }

    &__logo {
      width: 46px;
      height: 46px;
      border-radius: $wad4-border-radius-full;
      border: 1px solid $wad4-c-border-border-subtle;

      &--on-graphic {
        position: absolute;
        left: $wad4-spacing-4xl;
        bottom: -23px;
      }

      &--small {
        width: 40px;
        height: 40px;
      }

      &-img {
        border-radius: $wad4-border-radius-full;

        &--small {
          width: 40px;
          height: 40px;
        }
      }
    }

    &__body {
      padding: $wad4-spacing-4xl;
      display: flex;
      flex-direction: column;
      flex: 1;
      gap: $wad4-spacing-md;

      &--small {
        padding: $wad4-spacing-lg;
      }
    }

    &__time {
      color: $wad4-c-text-text-tertiery;
      font-family: $wad4-default-font-unbounded;
      font-size: 0.6875rem;
      line-height: 1.03rem;
    }

    &__link {
      text-decoration: none;

      &:hover {
        color: $wad4-c-text-text-brand-tertiery;
      }

      &:visited {
        color: var(--color-text-card);
      }
    }

    &__title {
      margin: 0;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      color: $wad4-c-text-text-primary;
      font-family: $wad4-default-font-unbounded;
      font-size: 1.125rem;
      font-style: normal;
      font-weight: $wad4-font-weight-semi-bold;
      line-height: 1.6rem;
      letter-spacing: -0.03rem;

      &--small {
        font-size: 1rem;
        line-height: 1.25rem;
      }

      .wad4-job-card--clickable:hover & {
        color: $wad4-c-text-text-brand-tertiery;
      }
    }

    &__data {
      display: flex;
      gap: $wad4-spacing-md;
    }

    &__info {
      margin: 0;
      color: $wad4-c-text-text-secondary;
      @include text-size-mixin('sm');

      &--light {
        color: $wad4-c-text-text-tertiery;
      }
    }

    &__chips {
      margin-top: auto;
      display: flex;
      flex-wrap: wrap;
      gap: $wad4-spacing-md;
    }
  }
</style>
